import { inject } from '@angular/core';
import { HttpInterceptorFn } from '@angular/common/http';
import { from, isObservable, switchMap } from 'rxjs';
import { FISERV_CONFIG, FiservConfig } from './fiserv.config';

async function resolveToken(config: FiservConfig): Promise<string> {
  if (!config.tokenFn) return '';
  const raw = config.tokenFn();
  if (isObservable(raw)) {
    return new Promise<string>((resolve, reject) =>
      raw.subscribe({ next: resolve, error: reject })
    );
  }
  return raw;
}

function resolveBranchId(config: FiservConfig): string | null {
  if (!config.branchId) return null;
  return typeof config.branchId === 'function'
    ? config.branchId()
    : config.branchId;
}

/**
 * Closure-based interceptor factory — preferred for standalone apps to avoid
 * inject() context issues when library and app share separate Angular instances.
 *
 * Usage (app.config.ts):
 *   const fiservConfig = { apiUrl: '...', tokenFn: () => store.get('token') };
 *   provideHttpClient(withInterceptors([createFiservInterceptorFn(fiservConfig)]))
 *   ...provideFiserv(fiservConfig)
 */
export function createFiservInterceptorFn(config: FiservConfig): HttpInterceptorFn {
  return (req, next) => {
    if (!req.url.startsWith(config.apiUrl)) return next(req);

    if (config.apiKey) {
      return next(req.clone({ setHeaders: { 'X-Api-Key': config.apiKey } }));
    }

    return from(resolveToken(config)).pipe(
      switchMap(token => {
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const branchId = resolveBranchId(config);
        if (branchId) headers['X-Branch-Id'] = branchId;
        return next(req.clone({ setHeaders: headers }));
      })
    );
  };
}

/**
 * DI-based functional interceptor — use when library and app share the SAME
 * Angular instance (e.g. library built/published as npm package).
 *
 * Usage (app.config.ts):
 *   provideHttpClient(withInterceptors([fiservAuthInterceptorFn]))
 */
export const fiservAuthInterceptorFn: HttpInterceptorFn = (req, next) => {
  const config = inject(FISERV_CONFIG);

  if (!req.url.startsWith(config.apiUrl)) {
    return next(req);
  }

  if (config.apiKey) {
    return next(req.clone({ setHeaders: { 'X-Api-Key': config.apiKey } }));
  }

  return from(resolveToken(config)).pipe(
    switchMap(token => {
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const branchId = resolveBranchId(config);
      if (branchId) headers['X-Branch-Id'] = branchId;
      return next(req.clone({ setHeaders: headers }));
    })
  );
};
